/**
 * Fires one impression when a listing card is actually seen.
 *
 * "Seen" is deliberately stricter than "mounted": a card rendered below the
 * fold, or flashed past during a fast scroll, was never really shown, and
 * counting it deflates every click-through rate. So the card must be at least
 * half visible for a continuous dwell before it counts.
 *
 * Costs nothing for an unsampled session -- no observer is created at all --
 * which matters because this runs on every card of every list in the product.
 */

import { useEffect, useRef } from 'react';
import {
  isImpressionSessionSampled,
  recordImpression,
  type ListingImpression,
} from '../utils/impression-queue';

/** Fraction of the card that must be on screen. */
const VISIBILITY_THRESHOLD = 0.5;

/** How long it must stay there. Long enough to exclude a fast scroll-past. */
const DWELL_MS = 400;

export function useListingImpression(
  ref: React.RefObject<Element | null>,
  impression: ListingImpression | null
) {
  // Read inside the observer callback so a re-render with new props does not
  // tear down and rebuild the observer on every scroll frame.
  const impressionRef = useRef(impression);
  // Written in an effect rather than during render: every read is inside the
  // observer callback or its dwell timer, both of which run after commit, so
  // they always see the committed value.
  useEffect(() => {
    impressionRef.current = impression;
  });

  const firedRef = useRef(false);

  useEffect(() => {
    // Unsampled sessions are the common case; do no work for them.
    if (!isImpressionSessionSampled()) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const element = ref.current;
    if (!element) return;

    let dwellTimer: ReturnType<typeof setTimeout> | null = null;

    const cancel = () => {
      if (dwellTimer) {
        clearTimeout(dwellTimer);
        dwellTimer = null;
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            cancel();
            continue;
          }
          if (firedRef.current || dwellTimer) continue;

          dwellTimer = setTimeout(() => {
            dwellTimer = null;
            const current = impressionRef.current;
            if (!current) return;
            firedRef.current = true;
            recordImpression(current);
            // One impression per card per page view; stop watching immediately
            // so a long scroll is not paying for observers it no longer needs.
            observer.disconnect();
          }, DWELL_MS);
        }
      },
      { threshold: VISIBILITY_THRESHOLD }
    );

    observer.observe(element);

    return () => {
      cancel();
      observer.disconnect();
    };
    // Keyed on the listing so a recycled card in a virtualised list counts the
    // new listing rather than inheriting the old one's fired flag.
  }, [ref, impression?.listing_id]);

  // Reset when the card is reused for a different listing.
  useEffect(() => {
    firedRef.current = false;
  }, [impression?.listing_id]);
}
